import {
    ArrowRoundTimeForwardIcon,
    DocsPlusIcon,
    People1PlusIcon,
    StarIcon,
    ToolPencilLineIcon,
} from "@skbkontur/icons";
import { ColumnStack, Fill, Fit, RowStack } from "@skbkontur/react-stack-layout";
import { Button, Hint, Input, Kebab, Link, MenuItem, Modal, Spinner, Toast } from "@skbkontur/react-ui";
import * as React from "react";

import { useCandyApiClient } from "../../../../../Common/CandyApiClient/CandyApiClientContext";
import { DraftPresentationType } from "../../../../../Common/CandyApiClient/Types/Presentations/DraftPresentationType";
import { Action, ActionType } from "../../Domain/Actions";
import { fixClientInfo } from "../../Domain/ClientInfo/FixClientInfo";
import { GfvInfo } from "../App/GfvInfo";
import { DeveloperHint } from "../DeveloperHint/DeveloperHint";
import { RequisitesProviderContext } from "../RequisitesProvider/RequisitesProvider";
import { RequisitesType } from "../RequisitesProvider/RequisitesSet";

import cn from "./GfvItem.less";

interface GfvItemProps {
    gfv: string;
    gfvInfo: undefined | GfvInfo;
    onChangeGfvInfo: (value: GfvInfo) => void;
    isFavorite: boolean;
    onChangeIsFavorite: (isFavorite: boolean) => void;
    onAddAction: (action: Action) => void;
    onGetOpenEditorUrl: (url: string) => string;
    getScenario?: () => string | undefined;
}

export function GfvItem({
    gfv,
    isFavorite,
    onAddAction,
    onChangeIsFavorite,
    onGetOpenEditorUrl,
    gfvInfo,
    onChangeGfvInfo,
    getScenario,
}: GfvItemProps): JSX.Element {
    const api = useCandyApiClient();
    const [creating, setCreating] = React.useState(false);
    const requisitesProviderContext = React.useContext(RequisitesProviderContext);

    const createEmptyDraftWithRequisites = async (): Promise<void> => {
        const requisites = await requisitesProviderContext?.requestRequisites("Создать черновик");
        if (requisites == undefined) {
            return;
        }
        if (requisites.type !== RequisitesType.FormsClientInfo) {
            Toast.push("Поддерживается создание только FormsClientInfo");
        }
        setCreating(true);
        try {
            const ns = "84de27f1-7cb4-4854-aa55-bd67699c939a";
            const response = await api.createEmptyDraft({ ns: ns, gfv: gfv, scenario: getScenario?.() });
            await api
                .forDraft(response)
                .setPresentation(DraftPresentationType.formsClientInfo, fixClientInfo(requisites.content));
            onAddAction({
                type: ActionType.OpenEmpty,
                dateString: new Date().toString(),
                gfv: gfv,
                editorUrl: response.editorUrl,
            });
            window.open(onGetOpenEditorUrl(response.editorUrl), "_blank");
        } catch (e) {
            checkAuthorizeOrToast(e);
        }
        setCreating(false);
    };

    const createEmptyDraft = async (): Promise<void> => {
        setCreating(true);
        try {
            const ns = "84de27f1-7cb4-4854-aa55-bd67699c939a";
            const response = await api.createEmptyDraft({ ns: ns, gfv: gfv, scenario: getScenario?.() });
            onAddAction({
                type: ActionType.OpenEmpty,
                dateString: new Date().toString(),
                gfv: gfv,
                editorUrl: response.editorUrl,
            });
            window.open(onGetOpenEditorUrl(response.editorUrl), "_blank");
        } catch (e) {
            checkAuthorizeOrToast(e);
        }
        setCreating(false);
    };

    const createEmptyDraftOnTestKontur = async (): Promise<void> => {
        setCreating(true);
        try {
            const url = `https://extern.testkontur.ru/AllForms/new_forms/new_showform.aspx?gfv=${gfv}`;
            window.open(onGetOpenEditorUrl(url), "_blank");
        } catch (e) {
            checkAuthorizeOrToast(e);
        }
        setCreating(false);
    };

    const [showInfoEditor, setShowInfoEditor] = React.useState(false);
    const [currentGfvInfo, setCurrentGfvInfo] = React.useState(
        () => gfvInfo ?? { description: "", shortDescription: "" }
    );

    const beginEditInfo = () => {
        setCurrentGfvInfo(gfvInfo ?? { description: "", shortDescription: "" });
        setShowInfoEditor(true);
    };

    return (
        <RowStack block baseline gap={2} className={cn("root")}>
            <Fill className={cn("header")}>
                <ColumnStack inline stretch>
                    <Fit>
                        <span className={cn("gfv")}>{gfv}</span>
                        <span className={cn("short-description")}> {gfvInfo?.shortDescription}</span>{" "}
                        <span className={cn("edit")}>
                            <Link icon={<ToolPencilLineIcon />} onClick={beginEditInfo} />
                        </span>
                    </Fit>
                    <Fit>
                        <span className={cn("description")}>{gfvInfo?.description}</span>
                    </Fit>
                </ColumnStack>

                {showInfoEditor && (
                    <Modal
                        ignoreBackgroundClick
                        onClose={() => {
                            setShowInfoEditor(false);
                        }}>
                        <Modal.Header>Редактировать информацию</Modal.Header>
                        <Modal.Body>
                            <div style={{ width: 700 }}>
                                <ColumnStack block stretch gap={4}>
                                    <Fit>
                                        <RowStack baseline block>
                                            <Fit style={{ width: 150 }}>Краткое название:</Fit>
                                            <Fit>
                                                <Input
                                                    width={200}
                                                    value={currentGfvInfo.shortDescription}
                                                    onValueChange={value =>
                                                        setCurrentGfvInfo({
                                                            ...currentGfvInfo,
                                                            shortDescription: value,
                                                        })
                                                    }
                                                />
                                            </Fit>
                                        </RowStack>
                                    </Fit>
                                    <Fit>
                                        <RowStack baseline block>
                                            <Fit style={{ width: 150 }}>Описание:</Fit>
                                            <Fit>
                                                <Input
                                                    width={500}
                                                    value={currentGfvInfo.description}
                                                    onValueChange={value =>
                                                        setCurrentGfvInfo({ ...currentGfvInfo, description: value })
                                                    }
                                                />
                                            </Fit>
                                        </RowStack>
                                    </Fit>
                                </ColumnStack>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <RowStack block gap={2}>
                                <Fit>
                                    <Button
                                        onClick={() => {
                                            onChangeGfvInfo(currentGfvInfo);
                                            setShowInfoEditor(false);
                                        }}
                                        use={"primary"}>
                                        Сохранить
                                    </Button>
                                </Fit>
                                <Fit>
                                    <Button
                                        onClick={() => {
                                            setShowInfoEditor(false);
                                        }}>
                                        Отменить
                                    </Button>
                                </Fit>
                            </RowStack>
                        </Modal.Footer>
                    </Modal>
                )}
            </Fill>
            <Fit>
                <Hint text={isFavorite ? "Убрать из избранного" : "Добавить в избранное"}>
                    <Link
                        icon={isFavorite ? <StarIcon weight="solid" /> : <StarIcon />}
                        onClick={() => onChangeIsFavorite(!isFavorite)}
                    />
                </Hint>
            </Fit>
            <Fit>
                {creating ? (
                    <Button use="link" icon={<Spinner type={"mini"} caption={""} dimmed />} disabled width="123px">
                        Открываем
                    </Button>
                ) : (
                    <Button use="link" onClick={createEmptyDraft} icon={<DocsPlusIcon />}>
                        Открыть пустой
                    </Button>
                )}
            </Fit>
            <Fit>
                <Kebab size="large">
                    <MenuItem icon={<ArrowRoundTimeForwardIcon />} disabled>
                        <DeveloperHint>Force build</DeveloperHint>
                    </MenuItem>
                    <MenuItem target="_blank" onClick={createEmptyDraftWithRequisites} icon={<People1PlusIcon />}>
                        Открыть пустой с реквизитами
                    </MenuItem>
                    <MenuItem target="_blank" onClick={createEmptyDraftOnTestKontur} icon={<DocsPlusIcon />}>
                        Открыть на тестовой
                    </MenuItem>
                </Kebab>
            </Fit>
        </RowStack>
    );
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function checkAuthorizeOrToast(e: any) {
    if (e.name.includes("HttpError") && e.statusCode === 401) {
        window.open("https://extern.testkontur.ru/", "_blank");
    } else {
        Toast.push("Ошибка при создании черновика");
    }
}
